import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import service from '@/services';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import { getIntegrationQueryKeys } from '../../integrationUtils';
import {
  IMPORT_STATE,
  type ImportHistoryItemT,
  type SalesforceInfo,
  type SyncConditionInfo,
} from '../types';

const mapSalesforceInfo = (data: unknown): SalesforceInfo[] => {
  if (!Array.isArray(data)) return [];

  const mapped = data
    .map((item) => {
      if (!item || typeof item !== 'object') return null;

      const info = item as {
        workspace_id?: string;
        alias?: string;
        user_id?: string;
        state?: IMPORT_STATE;
        import_state?: IMPORT_STATE;
        history?: ImportHistoryItemT[];
        is_account_sync?: boolean;
        account_sync?: boolean;
        auto_sync?: boolean;
        sync_condition_info?: SyncConditionInfo[];
      };

      if (!info.alias || !info.user_id) return null;

      return {
        workspace_id: info.workspace_id || info.alias,
        alias: info.alias,
        user_id: info.user_id,
        import_state:
          info.import_state || info.state || IMPORT_STATE.NOT_STARTED,
        history: info.history || [],
        account_sync: info.account_sync ?? info.is_account_sync ?? false,
        auto_sync: info.auto_sync || false,
        sync_condition_info: info.sync_condition_info || [],
      };
    })
    .filter(Boolean);

  return mapped as SalesforceInfo[];
};

const mapSingleSalesforceInfo = (data: unknown): SalesforceInfo | null => {
  if (!data || typeof data !== 'object') return null;

  const info = data as {
    workspace_id?: string;
    alias?: string;
    user_id?: string;
    state?: IMPORT_STATE;
    import_state?: IMPORT_STATE;
    history?: ImportHistoryItemT[];
    is_account_sync?: boolean;
    account_sync?: boolean;
    auto_sync?: boolean;
    sync_condition_info?: SyncConditionInfo[];
  };

  if (!info.alias || !info.user_id) return null;

  return {
    workspace_id: info.workspace_id || info.alias,
    alias: info.alias,
    user_id: info.user_id,
    import_state: info.import_state || info.state || IMPORT_STATE.NOT_STARTED,
    history: info.history || [],
    account_sync: info.account_sync ?? info.is_account_sync ?? false,
    auto_sync: info.auto_sync || false,
    sync_condition_info: info.sync_condition_info || [],
  };
};

const getSalesforceConnectError = (e: unknown): string => {
  const exception = e as AxiosError;

  if (exception.response?.status === 409) {
    return 'Salesforce with the consumer key is already connected to Syncly. Please check again!';
  }
  if (exception.response?.status === 403) {
    return 'Your consumer key and consumer secret cannot be validated. Please check again!';
  }

  return 'Your authorization information cannot be validated. Please check again!';
};

export const useSalesforce = (enabled: boolean) => {
  const queryClient = useQueryClient();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const workspaceAlias = currWorkspace?.alias;

  if (!workspaceAlias) throw new Error('Workspace alias is required');

  const queryKey = getIntegrationQueryKeys(
    SOURCE_TYPE.SALESFORCE,
    workspaceAlias,
  );

  const { data: salesforceOrganizations = [] } = useQuery({
    queryKey,
    queryFn: async () =>
      mapSalesforceInfo(
        await service.salesforce.getSalesforceInfo(workspaceAlias),
      ),
    enabled,
  });

  const { mutateAsync: connectSalesforceMutation } = useMutation({
    mutationFn: ({
      userId,
      password,
      consumerKey,
      consumerSecret,
    }: {
      userId: string;
      password: string;
      consumerKey: string;
      consumerSecret: string;
    }) =>
      service.salesforce.createSalesforceInfo(
        userId,
        password,
        consumerKey,
        consumerSecret,
        workspaceAlias,
      ),
    onSuccess: (data) => {
      const mapped = mapSingleSalesforceInfo(data);
      if (!mapped) return;

      queryClient.setQueryData<SalesforceInfo[]>(queryKey, (current = []) => [
        ...current,
        mapped,
      ]);
    },
  });

  const { mutateAsync: updateSalesforceMutation } = useMutation({
    mutationFn: ({
      userId,
      password,
      consumerKey,
      consumerSecret,
    }: {
      userId: string;
      password: string;
      consumerKey: string;
      consumerSecret: string;
    }) =>
      service.salesforce.updateSalesforceInfo(
        userId,
        password,
        consumerKey,
        consumerSecret,
        workspaceAlias,
      ),
    onSuccess: (data) => {
      const mapped = mapSingleSalesforceInfo(data);
      if (!mapped) return;

      queryClient.setQueryData<SalesforceInfo[]>(queryKey, (current = []) =>
        current.map((item) => (item.alias === mapped.alias ? mapped : item)),
      );
    },
  });

  const { mutate: disconnectSalesforceMutation } = useMutation({
    mutationFn: (salesforceAlias: string) =>
      service.salesforce.deleteSalesforceInfo(salesforceAlias),
    onSuccess: (_, salesforceAlias) => {
      queryClient.setQueryData<SalesforceInfo[]>(queryKey, (current = []) =>
        current.filter((item) => item.alias !== salesforceAlias),
      );
    },
  });

  const { mutate: syncSalesforceMutation } = useMutation({
    mutationFn: (salesforceAlias: string) =>
      service.salesforce.syncSalesforceInfo(salesforceAlias),
  });

  const { mutateAsync: updateAutoSyncMutation } = useMutation({
    mutationFn: ({
      salesforceAlias,
      sync,
    }: {
      salesforceAlias: string;
      sync: boolean;
    }) => service.salesforce.updateSalesforceAutoSync(salesforceAlias, sync),
    onSuccess: (_, variables) => {
      queryClient.setQueryData<SalesforceInfo[]>(queryKey, (current = []) =>
        current.map((item) =>
          item.alias === variables.salesforceAlias
            ? { ...item, auto_sync: variables.sync }
            : item,
        ),
      );
    },
  });

  const { mutateAsync: requestImportMutation } = useMutation({
    mutationFn: ({
      salesforceAlias,
      accountSync,
      taskSync,
      startDate,
      endDate,
      syncConditionInfo,
    }: {
      salesforceAlias: string;
      accountSync: boolean;
      taskSync: boolean;
      startDate?: string;
      endDate?: string;
      syncConditionInfo?: SyncConditionInfo[];
    }) =>
      service.salesforce.importSalesforceInfo(
        salesforceAlias,
        accountSync,
        taskSync,
        startDate,
        endDate,
        syncConditionInfo,
      ),
    onSuccess: (_, variables) => {
      queryClient.setQueryData<SalesforceInfo[]>(queryKey, (current = []) =>
        current.map((item) =>
          item.alias === variables.salesforceAlias
            ? { ...item, import_state: IMPORT_STATE.IMPORTING }
            : item,
        ),
      );
    },
  });

  return {
    salesforceOrganizations,
    connectSalesforceMutation,
    updateSalesforceMutation,
    disconnectSalesforceMutation,
    syncSalesforceMutation,
    updateAutoSyncMutation,
    requestImportMutation,
    getSalesforceConnectError,
  };
};
